import type { ButtonHTMLAttributes } from 'react'

export type ButtonVariant = 'primary' | 'warning' | 'secondary' | 'danger'

/** The preset's button classes (tailwind-preset.js). Export, never retype, so
 *  tests and call sites derive from one place. */
export const BUTTON_CLASSES: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  warning: 'btn-warning',
  secondary: 'btn-secondary',
  danger: 'btn-danger',
}
export const BUTTON_SIZE_SM = 'btn-sm'
/** Added to a secondary button for the chosen state of a toggle or tab. A
 *  toggle is not an action, so it never becomes a solid button: the view's one
 *  solid button stays its main action. Pair it with `aria-pressed` or
 *  `aria-selected`.
 *
 *  ⚠️ THE HOVER IS `enabled:hover:`, NOT `hover:`. `.btn-secondary`'s own hover
 *  is `:hover:not(:disabled)` (three classes' worth of specificity), which beats
 *  a plain `hover:` utility and would flash the fill color over the tint.
 *  `enabled:hover:` matches it, and utilities load after components. */
export const BUTTON_PRESSED =
  'border-brand-red-dark bg-brand-red-dark/10 text-brand-red-dark enabled:hover:bg-brand-red-dark/15'

/** One solid button per view: `primary`, or `warning` when the action sends or
 *  writes outside the platform. Everything else is `secondary`; `danger`
 *  starts something destructive. */
export function Button({
  variant = 'secondary',
  size = 'md',
  type = 'button',
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: 'md' | 'sm' }) {
  const cls = [BUTTON_CLASSES[variant], size === 'sm' ? BUTTON_SIZE_SM : '', className].filter(Boolean).join(' ')
  // eslint-disable-next-line react/button-has-type -- type defaults to 'button' above
  return <button type={type} className={cls} {...rest} />
}
